/**
 * Shares service — `record_shares`, a public link to one record. Whoever holds
 * the link's token can view the record (through the network app) until the
 * share expires or is archived.
 */

import type { ResultOf } from "@graphql-typed-document-node/core"
import { archiveShare, createShare, unarchiveShare } from "../gql/shares.js"
import type { Mutate, Query } from "../index.js"

export interface CreateShareInput {
  recordId: number
  /** ISO timestamp; omit or null for a share that never expires. */
  expiresAt?: string | null
  /** What the share shows, e.g. which fields are visible. */
  context?: Record<string, unknown> | null
}

/** A share's state after archiving or unarchiving. */
/** A share as `create` returns it: RecordShareFields, its token included. */
export type ShareRow = NonNullable<ResultOf<typeof createShare>["insert_record_shares_one"]>

/** What archive and unarchive return: the share's id and archive state. */
export type ShareArchiveState = NonNullable<
  ResultOf<typeof archiveShare>["update_record_shares_by_pk"]
>

// Lowercase letters minus the easily confused l and o.
const TOKEN_ALPHABET = "abcdefghijkmnpqrstuvwxyz"
const TOKEN_LENGTH = 10

/** A share token. The token alone grants access, so it comes from the
 *  platform's secure random source, with rejection sampling so every letter is
 *  equally likely. */
export function generateShareToken(): string {
  const limit = 256 - (256 % TOKEN_ALPHABET.length)
  let token = ""
  while (token.length < TOKEN_LENGTH) {
    for (const byte of crypto.getRandomValues(new Uint8Array(TOKEN_LENGTH))) {
      if (byte < limit && token.length < TOKEN_LENGTH) {
        token += TOKEN_ALPHABET[byte % TOKEN_ALPHABET.length]
      }
    }
  }
  return token
}

export function createSharesService({ mutate }: { query: Query; mutate: Mutate }) {
  /** Share a record. Returns the share, its token included. */
  const create = async (input: CreateShareInput): Promise<ShareRow> => {
    const data = await mutate(createShare, {
      object: {
        record_id: input.recordId,
        token: generateShareToken(),
        expires_at: input.expiresAt ?? null,
        context: input.context ?? null,
      },
    })
    const row = data.insert_record_shares_one
    if (!row) throw new Error(`Failed to share record ${input.recordId}`)
    return row
  }

  const setArchived = async (id: number, archived: boolean): Promise<ShareArchiveState> => {
    const data = archived
      ? await mutate(archiveShare, { id })
      : await mutate(unarchiveShare, { id })
    const row = data.update_record_shares_by_pk
    if (!row) throw new Error(`Share ${id} not found`)
    return row
  }

  /** Stop the link working. The share is kept and can be unarchived. */
  const archive = (id: number) => setArchived(id, true)
  const unarchive = (id: number) => setArchived(id, false)

  return { create, archive, unarchive }
}
